import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { Matrix4 } from 'three'

export function getRelativeMatrixOLD(referenceMatrix: Matrix4, offset: number) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  const offsetVector = toVector3([offset, 0, 0]).applyQuaternion(quaternion.normalize())

  position.add(offsetVector)

  newMatrix.compose(position, quaternion, scale)

  return newMatrix
}

import { LinkedItem } from '@types'
import { useAppStore } from '@store'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import { Quaternion, Vector3 } from 'three'

export function getRelativeMatrix(
  referenceMatrix: Matrix4,
  offset: number,
  referenceItem?: LinkedItem,
  heightOffset?: number,
  newItemTipoNormalizado?: string,
) {
  const newMatrix = referenceMatrix.clone()

  const position = toVector3()
  const quaternion = toQuaternion()
  const scale = toVector3()
  newMatrix.decompose(position, quaternion, scale)

  function getRegletaInvertedValue(value: number, morph: any) {
    if (!morph) return 0
    if (value >= morph.max) return morph.min
    if (value <= morph.min) return morph.max
    return morph.max + morph.min - value
  }
  // Detectar si el mueble de referencia es un rinconero
  const rinconeroPatterns = ['arp', 'alp', 'brp', 'blp', 'arb', 'alb', 'brb', 'blb']
  const isRinconero =
    referenceItem?.catalogID &&
    rinconeroPatterns.some((pattern) =>
      referenceItem.catalogID.toString().toLowerCase().includes(pattern),
    ) &&
    !referenceItem.catalogID.toString().toString().toLowerCase().includes('i')

  // Detectar si es KARB (arp, arb) - estos usan regleta directa, NO invertida
  const catalogIDLower = referenceItem?.catalogID?.toString().toLowerCase() || ''
  const isKARB = catalogIDLower.includes('arb') || catalogIDLower.includes('arp')
  // const isKBR = catalogIDLower.includes('kbr')

  // Casos especiales de posicionamiento vertical
  // Reglas simples:
  // 1. MBJ y MAL se ponen al lado de COL (horizontalmente) - lógica normal
  // 2. MAL encima de MBJ (verticalmente) - casos especiales con offset 0
  const isMBJToMAL = referenceItem?.tipoNormalizado === 'MBJ' && newItemTipoNormalizado === 'MAL'
  const isMALToMBJ = referenceItem?.tipoNormalizado === 'MAL' && newItemTipoNormalizado === 'MBJ'

  if (isRinconero) {
    // Calculamos la posición base (igual que en collideItems)
    const offsetVector = toVector3([offset, 0, 0]).applyQuaternion(quaternion)
    const newPosition = position.clone().add(offsetVector)

    // Ajustamos la rotación (90 grados en Y)
    const rotationAdjustment = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), -Math.PI / 2)

    // Combinamos la rotación actual con el ajuste
    const newQuaternion = quaternion.clone().multiply(rotationAdjustment)

    // Ajustamos la posición considerando la forma en L del rinconero
    // Para KARB: usar regleta directa (NO invertida). Para otros: usar regleta invertida
    const regletaMorph = referenceItem?.morphs?.find((m) => m.name === 'Regleta')
    const regletaValue = referenceItem?.regleta || 0
    const regletaInverted = getRegletaInvertedValue(regletaValue, regletaMorph)
    const regletaOffset = isKARB
      ? regletaValue // KARB: regleta directa (sin invertir)
      : regletaInverted // Otros: regleta invertida

    const KARB_OFFSET_ADJUSTMENT = isKARB ? -50 : 0
    const thickness = (referenceItem?.thickness || 0) + regletaOffset + KARB_OFFSET_ADJUSTMENT

    const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
    newPosition.add(zAxis.multiplyScalar(thickness))

    return new Matrix4().compose(newPosition, newQuaternion, scale)
  } else if (isMBJToMAL) {
    // Caso especial: MBJ -> MAL: colocar encima del MBJ
    // IMPORTANTE: Cuando se dibuja un MAL después de seleccionar un MBJ:
    // - X y Z deben ser iguales al MBJ seleccionado (offset === 0)
    // - Y viene del backend (no se calcula aquí, se sobrescribe en newGetDataFromGLB)
    //
    // NOTA: La posición Y calculada aquí será sobrescrita en newGetDataFromGLB con la altura del backend
    // del nuevo item. Solo calculamos X y Z aquí para el posicionamiento relativo.
    const referenceHeight = referenceItem?.height || 0

    // La posición de la matriz ya tiene las coordenadas correctas en 3D del MBJ seleccionado
    const refX = position.x
    const refY = position.y // Esta es la posición Y real del MBJ en 3D
    const refZ = position.z

    // Calcular nueva posición Y: la parte superior del MBJ es refY + referenceHeight
    // El MAL debe empezar justo ahí (su base Y será la parte superior del MBJ)
    // NOTA: Esta Y será reemplazada por la altura del backend en newGetDataFromGLB
    const newY = refY + referenceHeight

    let newX: number
    let newZ: number

    if (offset === 0) {
      // Sin desplazamiento horizontal: mantener X y Z exactamente iguales al MBJ seleccionado
      newX = refX
      newZ = refZ
    } else {
      // Con desplazamiento horizontal: aplicar el offset normal pero mantener Y encima del MBJ
      const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
        quaternion.normalize(),
      )
      const offsetPosition = position.clone().add(offsetVector)
      newX = offsetPosition.x
      newZ = offsetPosition.z
    }

    // Crear nueva matriz con la posición calculada (X y Z según el offset, Y será reemplazada por altura del backend)
    const newPosition = new Vector3(newX, newY, newZ)
    return new Matrix4().compose(newPosition, quaternion, scale)
  } else if (isMALToMBJ) {
    // Caso especial: MAL -> MBJ: colocar debajo del MAL
    // Si offset === 0: colocar directamente debajo sin desplazamiento horizontal
    // Si offset !== 0: colocar a la derecha/izquierda pero manteniendo la posición Y debajo del MAL
    const malX = position.x
    const malY = position.y // Esta es la posición Y real del MAL en 3D (base del MAL)
    const malZ = position.z

    // El MBJ/COL se coloca en la misma posición Y base que el MAL (debajo del MAL)
    const newY = malY

    let newX: number
    let newZ: number

    if (offset === 0) {
      // Sin desplazamiento horizontal: mantener X y Z exactamente iguales al MAL
      newX = malX
      newZ = malZ
    } else {
      // Con desplazamiento horizontal: aplicar el offset normal pero mantener Y debajo del MAL
      const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
        quaternion.normalize(),
      )
      const offsetPosition = position.clone().add(offsetVector)
      newX = offsetPosition.x
      newZ = offsetPosition.z
    }

    // console.log('🔍 [getRelativeMatrix] MAL->MBJ/COL:', {
    //   referenceItemTipo: referenceItem?.tipoNormalizado,
    //   newItemTipo: newItemTipoNormalizado,
    //   referenceItemY: malY.toFixed(2),
    //   calculatedNewY: newY.toFixed(2),
    //   newX: newX.toFixed(2),
    //   newZ: newZ.toFixed(2),
    //   offset,
    // })

    // Crear nueva matriz con la posición calculada (X y Z según el offset, Y siempre debajo del MAL)
    const newPosition = new Vector3(newX, newY, newZ)
    return new Matrix4().compose(newPosition, quaternion, scale)
  } else {
    // Lógica original para muebles normales
    // Esto incluye casos como COL->MBJ, MBJ->MBJ, etc.
    // COL se comporta como MBJ, así que cuando se dibuja un MBJ después de un COL,
    // debe usar la lógica normal (offset normal, no casos especiales de posicionamiento vertical)
    // Esto asegura que se coloque al lado del COL/MBJ, no encima o debajo

    const offsetVector = toVector3([offset, heightOffset ?? 0, 0]).applyQuaternion(
      quaternion.normalize(),
    )
    const newPos = position.clone().add(offsetVector)

    return newMatrix.compose(newPos, quaternion, scale)
  }
}
